import APTable, {
  APHeaderRow,
  APTableProps,
} from "../../../../components/APTable";
import { toDollars } from "../../../../helpers/currencies";
import {
  NonDeductibleYearCalculationResult,
  YearlyCalculation,
} from "../../../../types/Calculation";

type TableDataDefinition = {
  year: string;
  periodStart: string;
  periodEnd: string;
  days: string;
  rentalExGstLcc: string;
  employeeContribution: string;
  leaseBenefit: string;
  interest: string;
  depreciation: string;
  runningCosts: string;
  totalDeductible: string;
  nonDeductible: string;
  nonDeductibleCharge: string;
};

const yearlyCaculationToTableRow = (
  year: YearlyCalculation<NonDeductibleYearCalculationResult>
): Record<keyof TableDataDefinition, string> => {
  const {
    rentalExGstLcc,
    employeeContribution,
    leaseBenefit,
    interest,
    depreciation,
    runningCosts,
    totalDeductible,
    nonDeductible,
    nonDeductibleCharge,
  } = year.calculation.result;

  return {
    year: `${year.yearIndex + 1}`,
    periodStart: year.startDate,
    periodEnd: year.endDate,
    days: `${year.daysHeld}`,
    rentalExGstLcc: toDollars(rentalExGstLcc.result, 2),
    employeeContribution: toDollars(employeeContribution.result, 2),
    leaseBenefit: toDollars(leaseBenefit.result.result, 2),
    interest: toDollars(interest.result, 2),
    depreciation: toDollars(depreciation.result, 2),
    runningCosts: toDollars(runningCosts.result, 2),
    totalDeductible: toDollars(totalDeductible.result, 2),
    nonDeductible: toDollars(nonDeductible.result, 2),
    nonDeductibleCharge: toDollars(nonDeductibleCharge.result, 2),
  };
};

const yearlyCaculationToTableHeaderRow = (
  year: YearlyCalculation<NonDeductibleYearCalculationResult>
): Record<keyof TableDataDefinition, APHeaderRow> => {
  const {
    rentalExGstLcc,
    employeeContribution,
    leaseBenefit,
    interest,
    depreciation,
    runningCosts,
    totalDeductible,
    nonDeductible,
    nonDeductibleCharge,
  } = year.calculation.result;

  return {
    year: { title: "Year" },
    periodStart: { title: "Period Start" },
    periodEnd: { title: "Period End" },
    days: { title: "Days" },
    rentalExGstLcc: {
      title: rentalExGstLcc.name,
      titleTooltip: rentalExGstLcc.formula,
    },
    employeeContribution: {
      title: employeeContribution.name,
      titleTooltip: employeeContribution.formula,
    },
    leaseBenefit: {
      title: leaseBenefit.name,
      titleTooltip: leaseBenefit.result.formula,
    },
    interest: { title: interest.name, titleTooltip: interest.formula },
    depreciation: {
      title: "Depreciation",
      titleTooltip: depreciation.formula,
    },
    runningCosts: {
      title: runningCosts.name,
      titleTooltip: runningCosts.formula,
    },
    totalDeductible: {
      title: totalDeductible.name,
      titleTooltip: totalDeductible.formula,
    },
    nonDeductible: {
      title: nonDeductible.name,
      titleTooltip: nonDeductible.formula,
    },
    nonDeductibleCharge: {
      title: nonDeductibleCharge.name,
      titleTooltip: nonDeductibleCharge.formula,
    },
  };
};

const NonDeductibleTable = ({
  calculations,
  totalLeaseBenefit,
  totalInterest,
  totalDepreciation,
  totalDeductible,
  totalNonDeductible,
  totalNonDeductibleCharge,
}: {
  calculations: YearlyCalculation<NonDeductibleYearCalculationResult>[];
  totalLeaseBenefit: number;
  totalInterest: number;
  totalDepreciation: number;
  totalDeductible: number;
  totalNonDeductible: number;
  totalNonDeductibleCharge: number;
}) => {
  const rows = calculations.map((calc) => yearlyCaculationToTableRow(calc));

  const tableProps: APTableProps<TableDataDefinition> = {
    columns: [
      "year",
      "periodStart",
      "periodEnd",
      "days",
      "rentalExGstLcc",
      "employeeContribution",
      "leaseBenefit",
      "interest",
      "depreciation",
      "runningCosts",
      "totalDeductible",
      "nonDeductible",
      "nonDeductibleCharge",
    ],
    headerRows:
      calculations.length > 0
        ? yearlyCaculationToTableHeaderRow(calculations[0])
        : {},
    rows: rows,
    totalRow: {
      year: "Total",
      leaseBenefit: toDollars(totalLeaseBenefit, 2),
      interest: toDollars(totalInterest, 2),
      depreciation: toDollars(totalDepreciation, 2),
      totalDeductible: toDollars(totalDeductible, 2),
      nonDeductible: toDollars(totalNonDeductible, 2),
      nonDeductibleCharge: toDollars(totalNonDeductibleCharge, 2),
    },
    showTotalRow: true,
  };

  return <APTable {...tableProps} />;
};

export default NonDeductibleTable;
